import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";

import {
  type Column,
  ColumnDef,
  ColumnFiltersState,
  ColumnOrderState,
  ExpandedState,
  type FilterFnOption,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  type RowData,
  SortingState,
  Table,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";

import { CurrentViewContextProvider, useCurrentViewContext } from "../../hooks/use-current-view-context";

import {
  type DatatableColumn,
  type DatatableState,
  defaultDatatableState,
  groupRowsBySection,
  isSectionRow,
  isSubSectionRow,
  TSectionData,
} from "./datatable-types";

interface DatatableContextType<TData extends RowData> {
  table: Table<TData>;
  columns: DatatableColumn<TData>[];
  state: DatatableState;
  setState: (state: Partial<DatatableState>) => void;

  getConfigurationForColumn: (columnId: string) => DatatableColumn<TData> | undefined;
}

// We use a type assertion here because React's createContext doesn't directly support generics
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const DatatableContext = createContext<DatatableContextType<any>>(undefined as any);

export interface DataTableContextProviderProps<TData extends RowData> {
  viewKey: string;
  workspaceId: string;
  children: ReactNode;
  data: TData[];
  columns: DatatableColumn<TData>[];
  baseState?: DatatableState;
  debugTable?: boolean;
  synchToApi?: boolean;

  globalFilterFn?: FilterFnOption<TSectionData<TData>>;
  getColumnCanGlobalFilter?: (column: Column<TSectionData<TData>, unknown>) => boolean;
}

// Wrapper to make sure the current view context is available if synchToApi is true
export const DataTableContextProvider = <TData extends RowData>(props: DataTableContextProviderProps<TData>) => {
  if (props.synchToApi) {
    return (
      <CurrentViewContextProvider viewKey={props.viewKey} workspaceId={props.workspaceId} baseState={props.baseState}>
        <InitDatatableContextProviderFromAPI {...props} />
      </CurrentViewContextProvider>
    );
  }

  return (
    <InternalDataTableContextProvider
      {...props}
      initialState={props.baseState ?? defaultDatatableState}
      saveDatatableState={() => {}}
    />
  );
};

// Wrapper to separate out the logic to init from and synch to the api
export const InitDatatableContextProviderFromAPI = <TData extends RowData>(
  props: DataTableContextProviderProps<TData>,
) => {
  const { datatableState, saveDatatableState } = useCurrentViewContext();

  // Memoize the initial values of the context to avoid unnecessary re-renders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialDatatableState = useMemo(() => datatableState, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialSaveDatatableState = useMemo(() => saveDatatableState, []);

  return (
    <InternalDataTableContextProvider
      {...props}
      initialState={initialDatatableState}
      saveDatatableState={initialSaveDatatableState}
    />
  );
};

interface InternalTableStateProviderProps<TData extends RowData> extends DataTableContextProviderProps<TData> {
  initialState: DatatableState;
  saveDatatableState: (newDatatableState: DatatableState) => void;
}

const InternalDataTableContextProvider = <TData extends RowData>({
  children,
  debugTable = false,

  globalFilterFn,
  getColumnCanGlobalFilter,

  initialState,
  saveDatatableState,

  ...props
}: InternalTableStateProviderProps<TData>) => {
  const mountedRef = useRef(false);

  // We use useState to only set the initial values of the context and avoid re-rendering the table
  const [data, _setData] = useState<TData[]>(props.data);
  const [columns, _setColumns] = useState<DatatableColumn<TData>[]>(props.columns);

  const [showColumnHeaders, setShowColumnHeaders] = useState(initialState.showColumnHeaders);
  const [sectionColumnId, setSectionColumnId] = useState<string | null | undefined>(initialState.sectionColumnId);
  const [subSectionColumnId, setSubSectionColumnId] = useState<string | null | undefined>(
    initialState.subSectionColumnId,
  );
  const [globalFilter, setGlobalFilter] = useState<string>(initialState.globalFilter);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialState.columnFilters);
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(initialState.columnOrder);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(initialState.columnVisibility);
  const [sorting, setSorting] = useState<SortingState>(initialState.sorting);
  const [expanded, setExpanded] = useState<ExpandedState>(initialState.expanded);

  const tableData = useMemo(() => {
    if (sectionColumnId) {
      return groupRowsBySection(data, columns, sectionColumnId, subSectionColumnId);
    }
    return data;
  }, [data, columns, sectionColumnId, subSectionColumnId]);

  const localState = useMemo(() => {
    return {
      showColumnHeaders,
      sectionColumnId,
      subSectionColumnId,
      columnFilters,
      globalFilter,
      columnOrder,
      columnVisibility,
      sorting,
      expanded,
    };
  }, [
    showColumnHeaders,
    sectionColumnId,
    subSectionColumnId,
    columnFilters,
    globalFilter,
    columnOrder,
    columnVisibility,
    sorting,
    expanded,
  ]);

  const table = useReactTable<TSectionData<TData>>({
    debugTable,
    data: tableData,
    // columns are defined for TData, but the table rows may be wrapped in SectionData/DoubleSectionData<TData>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    columns: columns.map((c) => c.getColumnDef()) as ColumnDef<TSectionData<TData>, any>[],
    state: localState,

    filterFromLeafRows: true,

    getCoreRowModel: getCoreRowModel<TSectionData<TData>>(),
    getSortedRowModel: getSortedRowModel<TSectionData<TData>>(),
    getFilteredRowModel: getFilteredRowModel<TSectionData<TData>>(),
    getExpandedRowModel: getExpandedRowModel<TSectionData<TData>>(),

    onGlobalFilterChange: setGlobalFilter,
    onColumnFiltersChange: setColumnFilters,
    onColumnOrderChange: setColumnOrder,
    onColumnVisibilityChange: setColumnVisibility,
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,

    globalFilterFn: globalFilterFn,
    getColumnCanGlobalFilter: getColumnCanGlobalFilter,

    getRowCanExpand: (row) => {
      if (isSectionRow(row.original)) {
        return row.original.data.length > 0;
      }

      if (isSubSectionRow(row.original)) {
        return row.original.data.length > 0;
      }

      return false;
    },
    getSubRows: (originalRow) => {
      if (isSectionRow(originalRow)) {
        return originalRow.data;
      }

      if (isSubSectionRow(originalRow)) {
        return originalRow.data;
      }

      return undefined;
    },

    defaultColumn: {
      minSize: 0,
      size: Number.MAX_SAFE_INTEGER,
      maxSize: Number.MAX_SAFE_INTEGER,
    },
  });

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    saveDatatableState(localState);
    // only run when state changes, not when the hooks change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localState]);

  const setState = (newState: Partial<DatatableState>) => {
    if (newState.sectionColumnId !== undefined) {
      setSectionColumnId(newState.sectionColumnId);

      if (newState.sectionColumnId === subSectionColumnId) {
        setSubSectionColumnId(null);
      }

      if (newState.sectionColumnId === null) {
        setSubSectionColumnId(null);
      }
    }

    if (localState.sectionColumnId || newState.sectionColumnId) {
      if (newState.subSectionColumnId !== undefined) {
        setSubSectionColumnId(newState.subSectionColumnId);
      }
    }

    if (newState.showColumnHeaders !== undefined) {
      setShowColumnHeaders(newState.showColumnHeaders);
    }
    if (newState.columnFilters) {
      setColumnFilters(newState.columnFilters);
    }
    if (newState.globalFilter || newState.globalFilter === "") {
      setGlobalFilter(newState.globalFilter);
    }
    if (newState.columnOrder) {
      setColumnOrder(newState.columnOrder);
    }
    if (newState.columnVisibility) {
      setColumnVisibility(newState.columnVisibility);
    }
    if (newState.sorting) {
      setSorting(newState.sorting);
    }
    if (newState.expanded) {
      setExpanded(newState.expanded);
    }
  };

  return (
    <DatatableContext.Provider
      value={{
        table,
        columns,
        state: localState,
        setState,
        getConfigurationForColumn: (columnId: string) => {
          return columns.find((c) => c.columnId === columnId);
        },
      }}
    >
      {children}
    </DatatableContext.Provider>
  );
};

export const useDatatableContext = <TData extends RowData>(): DatatableContextType<TData> => {
  const context = useContext(DatatableContext);

  if (!context) {
    throw new Error("useDataTableContext must be used within a DataTableContextProvider");
  }

  return context as DatatableContextType<TData>;
};

export const useUnsafeDatatableContext = <TData extends RowData>(): DatatableContextType<TData> | null => {
  return useContext(DatatableContext);
};
